import React, { useCallback, useEffect, useState } from 'react'
import { VaultHero } from '../../Home/Vault/styles'
import { ForceFullWidth } from '../../../shared_components/Hero/styled'
import { MaxWidth } from '../../../shared_components/Page'
import { Box } from '../../../shared_components/UI/Box'
import { H2 } from '../../../shared_components/Library/Typeography'
import { useHistory } from 'react-router-dom'
import { StakingTarget } from '../types'
import { TokensAmount, VaultsAPY } from '../../Home/Vault'
import styled from 'styled-components'
import {
  GraphicAsset1,
  GraphicAsset2,
  GraphicAssetSquare,
} from './GraphicAssets'
import { useStakingContext } from '../../../contexts/StakingContext'

const tokenRewards = 21500000

function StakingHero({
  target,
  scrollToStake,
}: {
  target: StakingTarget
  scrollToStake: () => void
}) {
  const { calculateBonusApy, tokenApy } = useStakingContext()

  const topBaseApy = tokenApy[2]
  const topBonusApy = calculateBonusApy(2)

  return (
    <div>
      <VaultHero>
        <ForceFullWidth>
          <MaxWidth className="vault-hero-content" maxWidth={1600}>
            <HeroBrandBackground>
              <img
                className="vault-lightning"
                src="/zap-vault-hero-lightning.png"
              />
              <BottomGraphicsCont>
                <GraphicAsset2 className="graphic-asset-2" />
                <TokenSpinner />
              </BottomGraphicsCont>
            </HeroBrandBackground>
            <StakingHeroCont>
              <TitleBox ig dr z={1}>
                <TitleCont g={20} dc fu w="100%" mw="536px">
                  <H2 ff="Proxima Nova" s="4XL" c="text-invert" fw="bolder" uc>
                    {target === 'tokens'
                      ? '$ZAP Token rewards available for stakers'
                      : 'Get more apy than you know what to do with'}
                  </H2>
                  {target === 'tokens' && <TokensAmount total={tokenRewards} />}
                  {target === 'vaults' && topBonusApy && (
                    <VaultsAPY
                      value={`${(topBaseApy + topBonusApy).toFixed(2)}%`}
                    />
                  )}
                  {/*<P s="XL" fw="semibold" c="text-invert">*/}
                  {/*  {target === 'tokens'*/}
                  {/*    ? '$ZAP Token rewards available for stakers'*/}
                  {/*    : 'The best current APY for VAULT holders'}*/}
                  {/*</P>*/}
                  {/*<ThemedButton*/}
                  {/*  $styleType="primary-invert"*/}
                  {/*  onClick={scrollToStake}*/}
                  {/*>*/}
                  {/*  Stake {target === 'tokens' ? 'tokens' : 'VAULTS'}*/}
                  {/*</ThemedButton>*/}
                </TitleCont>
              </TitleBox>
              <HeroRight target={target} />
            </StakingHeroCont>
          </MaxWidth>
        </ForceFullWidth>
      </VaultHero>
    </div>
  )
}

export function HeroRight({
  target,
  invertColors = false,
}: {
  target: StakingTarget
  invertColors?: boolean
}) {
  const [hasLoaded, setHasLoaded] = useState(false)
  const videoSrc = `/video/hero/staking/${target}.webm`
  // transparent .webm doesnt work on mobile. transparecy turns into a black blob
  const srcMobileFallback = `/video/hero/staking/${target}.png`

  useEffect(() => {
    setHasLoaded(false)
  }, [videoSrc])

  // FIXME: seems the tokens video is not perfectly looped. there is a glitch at the end of the loop that makes it flash
  // i think its not a code problem, because the vaults video loops perfectly
  const VideoBlock = useCallback(() => {
    return (
      <VideoBlockCont>
        <DesktopOnly>
          <HomeVideo
            autoPlay
            muted
            loop
            playsInline
            onCanPlayThrough={() => setHasLoaded(true)}
          >
            <source src={videoSrc} type="video/webm" />
            <img src={srcMobileFallback} alt="asset" />
          </HomeVideo>
        </DesktopOnly>
        <MobileOnly>
          <img src={srcMobileFallback} alt="asset" />
        </MobileOnly>
        <GraphicsBox>
          <GraphicAsset1 className="graphic-asset-1" />
          <GraphicAssetSquare c="black" className="graphic-asset-square-1" />
          <GraphicAssetSquare c="#FFFFF0" className="graphic-asset-square-2" />
          <GraphicAssetSquare c="#FFFFF0" className="graphic-asset-square-3" />
          <GraphicAssetSquare c="#FFFFF0" className="graphic-asset-square-4" />
        </GraphicsBox>
      </VideoBlockCont>
    )
  }, [srcMobileFallback, videoSrc])

  return (
    <RightSectionContainer>
      {/*{!hasLoaded && (*/}
      {/*  <LoadingContainer c minW="50vw" minH="300px">*/}
      {/*    <AnimatedLoader onlyWhite size={80} />*/}
      {/*  </LoadingContainer>*/}
      {/*)}*/}
      <VideoBlock />
    </RightSectionContainer>
  )
}

const MobileOnly = styled.div`
  display: block;

  @media screen and (min-width: 999px) {
    display: none;
  }
`

const DesktopOnly = styled.div`
  display: block;

  @media screen and (max-width: 1000px) {
    display: none;
  }
`

const RightSectionContainer = styled(Box)`
  flex: 1;
  position: relative;

  @media screen and (max-width: 1000px) {
    position: unset;
  }
`

const LoadingContainer = styled(Box)`
  height: 100%;
  @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.expanded}px) {
    min-height: 500px;
  }
  @media screen and (min-width: ${({ theme }) =>
      theme.breakpoints.expandedPlus}px) {
    min-height: 690px;
    max-height: 690px;
  }
`

export const HomeVideo = styled.video`
  display: block;
  width: 100%;
  width: auto;
  height: auto;
  object-fit: cover;
  transform: scale(1.5);

  @media screen and (max-width: 1000px) {
    transform: none;
  }
`

const StakingHeroCont = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  top: -80px;
  left: 0;
  right: 0;
  min-height: calc(648px + 80px + 80px); // height of content + gutters/padding
  width: 100%;
  z-index: 1;

  @media (max-width: 1000px) {
    flex-direction: column;
    align-items: flex-start;
    height: 872px;
    min-height: auto;

    overflow-y: clip; // this clip worked but might not be the best. if you change it, make sure on mobile the sides of the assets dont get cut off
    overflow-x: unset; // clip only works with this
  }
`

const HeroBrandBackground = styled.div`
  background: ${({ theme }) => theme.colors.brandPrimary};
  position: absolute;
  top: -80px;
  left: 0;
  right: 0;
  height: 100%;
  width: 100%;
  //z-index: 1;
  overflow: hidden;

  --dotSize: 0.5px;

  ::before {
    /* Cover our element */
    content: '';
    position: absolute;
    inset: 0;

    /* Dotted background */
    background-image: radial-gradient(
      circle at center,
      black var(--dotSize),
      transparent 0
    );
    background-size: 5px 5px;
    background-repeat: round;
    background-position:
      0 0,
      0.65rem 0.65rem;
    mask-image: linear-gradient(
      135deg,
      rgba(8, 8, 8, 0) 20.85%,
      #ffff00 74.06%
    );

    z-index: 0;
  }
`

const GraphicsBox = styled(Box)`
  position: absolute;
  bottom: 0;
  right: 0;
  height: 100%;
  width: 100%;
  z-index: -1;

  .graphic-asset-1 {
    position: absolute;
    right: 0;
    top: 0;
    animation-name: up-down;
    animation-duration: 10s;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;

    @media screen and (max-width: 600px) {
      right: 40px;
    }
    @media screen and (max-width: 1000px) {
      top: -560px;
    }
  }
  .graphic-asset-square-1 {
    position: absolute;
    right: 290px;
    top: 110px;
    animation-name: grow-shrink;
    animation-duration: 5s;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;
  }
  .graphic-asset-square-2 {
    position: absolute;
    right: 580px;
    left: 160px;
    animation-name: grow-shrink;
    animation-duration: 5s;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;
  }

  .graphic-asset-square-3 {
    position: absolute;
    bottom: 120px;
    left: 260px;
    animation-name: grow-shrink;
    animation-duration: 5s;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;
  }
  .graphic-asset-square-4 {
    position: absolute;
    bottom: 80px;
    right: 40px;
    animation-name: grow-shrink;
    animation-duration: 5s;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;
  }

  @keyframes grow-shrink {
    0% {
      transform: scale(0.8);
    }
    50% {
      transform: scale(1);
    }
    100% {
      transform: scale(0.8);
    }
  }

  @keyframes up-down {
    0% {
      transform: translateY(-80px);
    }
    50% {
      transform: translateY(20px);
    }
    100% {
      transform: translateY(-80px);
    }
  }
`

const ParallaxSpan = styled.span<{ x: number }>`
  transform: translateX(${({ x }) => x}px);
`

const BottomGraphicsCont = styled(Box)`
  position: absolute;
  bottom: 0;
  right: 0;
  height: 100%;
  width: 100%;

.token-single video {
    position: absolute;
    bottom: -62px;
    right: -40px;
    height: 180px;

    left: 58%;
    transform: translateX(calc(100% - 50%)) rotateZ(325deg);

    @media screen and (max-width: 600px) {
      bottom: -20px;
      left: 40%;
      height: 120px;
    }

    @media screen and (max-width: 400px) {
      bottom: -20px;
      left: 30%;
      height: 100px;
    }
  }
  
  .token-single img {
    position: absolute;
    bottom: 10px;
    right: 2%;
    height: 30px;
  }

  .graphic-asset-2 { 
    position: absolute;
  bottom: -40px;
    right: 30%;
    animation-name: left-right;
    animation-duration: 15s;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;

    @media screen and (min-width: 1400px) {
  right: 50%;
  }

    @media screen and (max-width: 600px) {
      bottom: -30px;
    height: 80px;
  }

@keyframes left-right {
    0% {
      transform: translateX(-10px);
    }
    50% {
      transform: translateX(-300%);
    }
    100% {
      transform: translateX(-10px);
    }
`

const VideoBlockCont = styled.div`
  position: absolute;
  width: 100%;
  transform: translateY(-50%);
  margin-top: 60px;

  @media screen and (min-width: 768px) and (max-width: 1000px) {
    width: 80%;
    bottom: 0;
    left: auto;
    right: 10%;
    margin-top: auto;
    transform: none;
  }

  @media screen and (max-width: 768px) {
    width: 90%;
    bottom: 0;
    left: auto;
    right: 0;
    margin-top: auto;
    transform: none;
  }

  @media screen and (max-width: 600px) {
    width: 140%;
    right: -20%;
    bottom: 0;
    left: auto;
    margin-top: auto;
    transform: none;
  }

  img {
    position: absolute;
    bottom: 70px;
  }
`

const TitleBox = styled(Box)`
  @media screen and (max-width: 1000px) {
    padding-top: calc(80px + 48px); // header height + spacing
  }
`

const TitleCont = styled(Box)`
  @media screen and (max-width: 1000px) {
    width: 75%;
  }
`

export const TokenSpinner = ({ className }: { className?: string }) => {
  return (
    <div className={`token-single ${className}`}>
      <DesktopOnly>
        <video autoPlay loop muted playsInline>
          <source
            src="/video/hero/staking/token-single.webm"
            type="video/webm"
          />
        </video>
      </DesktopOnly>
      <MobileOnly>
        <img src="/video/hero/staking/token-single.svg" alt="token spinner" />
      </MobileOnly>
    </div>
  )
}

export default StakingHero
